import { Head, Link, router, usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { AppLayout } from "../../components/layout/AppLayout"
import { buttonVariants } from "../../components/ui/button"
import { cn } from "../../lib/cn"
import type { SharedPageProps } from "../../types/shared"

type UserRow = {
  id: number
  email: string
  created_at: string
  updated_at: string
}

type UsuariosIndexProps = {
  users: UserRow[]
}

export default function UsuariosIndex({ users }: UsuariosIndexProps) {
  const currentUserId = usePage<SharedPageProps>().props.auth.user?.id

  function handleDelete(userId: number) {
    const ok = window.confirm("Tem certeza que deseja excluir este usuário?")
    if (!ok) return
    router.delete(`/usuarios/${userId}`)
  }

  return (
    <>
      <Head title="Usuários" />
      <AppLayout>
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-white">Usuários</h1>
            <p className="text-slate-400">Administração de usuários (base para permissionamento futuro).</p>
          </div>

          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-slate-400">{users.length} usuário(s)</p>
            <Link href="/usuarios/novo" className={buttonVariants()}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Usuário
            </Link>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">E-mail</th>
                  <th className="px-4 py-3 font-medium">Criado em</th>
                  <th className="px-4 py-3 font-medium">Atualizado em</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={4}>
                      Nenhum usuário cadastrado.
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 text-slate-100">
                        {user.email}
                        {currentUserId === user.id && (
                          <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">você</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {user.created_at ? new Date(user.created_at).toLocaleString("pt-BR") : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {user.updated_at ? new Date(user.updated_at).toLocaleString("pt-BR") : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Link
                            href={`/usuarios/${user.id}/editar`}
                            className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}
                          >
                            Editar
                          </Link>
                          <button
                            type="button"
                            className={cn(buttonVariants({ variant: "outline" }), "border-rose-700 text-rose-300 hover:bg-rose-900/20")}
                            onClick={() => handleDelete(user.id)}
                            disabled={currentUserId === user.id}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
